// src/component/Profile.js
import React, { useState, useEffect, useCallback } from 'react';
import {
    FaUserCircle, FaEdit, FaSave, FaTimes, FaSpinner,
    FaExclamationTriangle, FaLock // Added FaLock for the button
} from 'react-icons/fa';
import { toast } from 'react-toastify';

import apiClient from '../api/apiClient'; // Adjust path if needed
import { useAuth } from '../context/AuthContext'; // Adjust path if needed
// *** 1. IMPORT THE PASSWORD MODAL ***
import ChangePasswordModal from './ChangePasswordModal'; // Adjust path if needed

const Profile = () => {
    const { user } = useAuth();

    // State
    const [profileData, setProfileData] = useState(null);
    const [editData, setEditData] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState(null);
    // *** 2. ADD STATE FOR PASSWORD MODAL VISIBILITY ***
    const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);

    // Fetch profile data (no changes needed here)
    const fetchProfile = useCallback(async () => {
        if (!user) return;
        setIsLoading(true);
        setError(null);
        try {
            const response = await apiClient.get('/profile');
            setProfileData(response.data);
        } catch (err) {
            console.error("Profile: Error fetching profile:", err);
            setError(err.response?.data?.error || 'Failed to load profile.');
        } finally {
            setIsLoading(false);
        }
    }, [user]);

    useEffect(() => {
        fetchProfile();
    }, [fetchProfile]);

    // Handle edit button click (no changes needed here)
    const handleEdit = () => {
        setEditData({
            fullName: profileData?.full_name || '',
            email: profileData?.email || '',
            phoneNumber: profileData?.phone_number || '',
            addressStreet: profileData?.address_street || '',
            addressCity: profileData?.address_city || '',
            addressState: profileData?.address_state || '',
            addressZip: profileData?.address_zip || '',
        });
        setIsEditing(true);
    };

    // Handle cancel button click (no changes needed here)
    const handleCancel = () => {
        setIsEditing(false);
        setEditData(null);
        setError(null);
    };

    // Handle input changes in edit mode (no changes needed here)
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setEditData(prevData => ({ ...prevData, [name]: value, }));
    };

    // Handle save button click (no changes needed here)
    const handleSave = async (e) => {
        e.preventDefault();
        if (!editData) return;
        setIsSaving(true);
        setError(null);
        try {
            const payload = { fullName: editData.fullName, email: editData.email, phoneNumber: editData.phoneNumber, addressStreet: editData.addressStreet, addressCity: editData.addressCity, addressState: editData.addressState, addressZip: editData.addressZip };
            const response = await apiClient.put('/profile', payload);
            setProfileData(response.data);
            setIsEditing(false);
            setEditData(null);
            toast.success("Profile updated successfully!");
        } catch (err) {
            console.error("Profile: Error saving profile:", err);
            const errorMsg = err.response?.data?.error || 'Failed to save profile.';
            setError(errorMsg);
            toast.error(`Update failed: ${errorMsg}`);
        } finally {
            setIsSaving(false);
        }
    };

    // --- Render Helper for Displaying Fields (View Mode) --- (no changes)
    const renderField = (label, value, isDate = false) => ( <div style={styles.fieldGroup}> <label style={styles.label}>{label}:</label> <span style={styles.value}>{value ? (isDate ? new Date(value).toLocaleDateString() : value) : <span style={styles.notSet}>Not Set</span>}</span> </div> );

    // --- Render Helper for Input Fields (Edit Mode) --- (no changes)
    const renderInput = (label, name, type = 'text', required = false) => ( <div style={styles.fieldGroup}> <label htmlFor={name} style={styles.label}>{label}: {required && '*'}</label> <input type={type} id={name} name={name} value={editData ? editData[name] : ''} onChange={handleInputChange} style={styles.input} required={required} disabled={isSaving} /> </div> ); // Added disabled={isSaving}

    // --- Loading / Error States --- (no changes)
    if (isLoading) { return <div style={styles.container}><FaSpinner className="fa-spin" size={30} /> Loading Profile...</div>; }
    if (error && !isEditing) { return <div style={styles.container}><FaExclamationTriangle color="red"/> Error loading profile: {error}</div>; }
    if (!profileData && !isLoading) { return <div style={styles.container}>Could not load profile data.</div>; }

    // --- Main Render ---
    return (
        <div style={styles.container}>
            <div style={styles.profileCard}>
                {/* Header Section (no changes) */}
                <div style={styles.header}>
                    <FaUserCircle size={50} style={styles.avatar} />
                    <h1 style={styles.title}>Your Profile</h1>
                    {!isEditing && ( <button onClick={handleEdit} style={styles.editButton} title="Edit Profile"> <FaEdit /> Edit </button> )}
                </div>

                {/* View or Edit Mode */}
                {isEditing ? (
                    // --- Edit Form (no changes) ---
                    <form onSubmit={handleSave}>
                        {renderInput('Full Name', 'fullName', 'text', true)}
                        {renderInput('Email', 'email', 'email')}
                        {renderInput('Phone Number', 'phoneNumber', 'tel')}
                        <h3 style={styles.sectionTitle}>Address</h3>
                        {renderInput('Street', 'addressStreet')}
                        {renderInput('City', 'addressCity')}
                        {renderInput('State/Province', 'addressState')}
                        {renderInput('ZIP/Postal Code', 'addressZip')}
                        {error && !isSaving && ( <div style={styles.errorBox}><FaExclamationTriangle/> {error}</div> )}
                        <div style={styles.buttonGroup}>
                            <button type="button" onClick={handleCancel} style={{ ...styles.actionButton, ...styles.cancelButton }} disabled={isSaving} > <FaTimes style={{marginRight: '5px'}}/> Cancel </button>
                            <button type="submit" style={{ ...styles.actionButton, ...styles.saveButton }} disabled={isSaving} > {isSaving ? ( <><FaSpinner className="fa-spin" style={{marginRight: '5px'}}/> Saving...</> ) : ( <><FaSave style={{marginRight: '5px'}}/> Save Changes</> )} </button>
                        </div>
                    </form>
                ) : (
                    // --- View Mode (Modified) ---
                    <div>
                        {/* Profile Fields (no changes) */}
                        {renderField('Full Name', profileData?.full_name)}
                        {renderField('Email', profileData?.email)}
                        {renderField('Phone Number', profileData?.phone_number)}
                        {renderField('Member Since', profileData?.created_at, true)}
                        <h3 style={styles.sectionTitle}>Address</h3>
                        {renderField('Street', profileData?.address_street)}
                        {renderField('City', profileData?.address_city)}
                        {renderField('State/Province', profileData?.address_state)}
                        {renderField('ZIP/Postal Code', profileData?.address_zip)}

                        {/* *** 3. UPDATE Change Password Button *** */}
                        <div style={{marginTop: '30px', textAlign: 'center'}}>
                             <button
                                style={{...styles.actionButton, ...styles.secondaryButton}}
                                onClick={() => setIsPasswordModalOpen(true)} // Set state to true
                             >
                                 <FaLock style={{marginRight: '8px'}} /> Change Password
                             </button>
                        </div>
                    </div>
                )}
            </div>

             {/* *** 4. RENDER THE PASSWORD MODAL *** */}
             <ChangePasswordModal
                isOpen={isPasswordModalOpen}
                onClose={() => setIsPasswordModalOpen(false)} // Set state to false
             />
        </div>
    );
};

// --- Styles --- (Assume they exist as defined in previous step)
// Make sure the styles object includes all necessary styles for both
// the profile page AND the password modal (like modalOverlayStyle, passwordModal styles, etc.)
const styles = {
    container: { padding: '30px', display: 'flex', justifyContent: 'center', alignItems: 'flex-start', minHeight: 'calc(100vh - 150px)', backgroundColor: '#f0f4f8', },
    profileCard: { backgroundColor: '#fff', padding: '30px 40px', borderRadius: '10px', boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)', width: '100%', maxWidth: '700px', },
    header: { display: 'flex', alignItems: 'center', marginBottom: '30px', paddingBottom: '20px', borderBottom: '1px solid #eee', position: 'relative', },
    avatar: { color: '#ccc', marginRight: '20px', },
    title: { fontSize: '1.8rem', color: '#1a237e', fontWeight: '600', margin: 0, flexGrow: 1, },
    editButton: { background: 'none', border: '1px solid #ddd', color: '#555', padding: '8px 15px', borderRadius: '5px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px', fontSize: '0.9rem', transition: 'all 0.2s ease', position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)', },
    sectionTitle: { fontSize: '1.2rem', color: '#34495e', marginTop: '30px', marginBottom: '15px', paddingBottom: '5px', borderBottom: '1px dashed #eee', },
    fieldGroup: { marginBottom: '18px', display: 'flex', flexDirection: 'column', },
    label: { fontWeight: '600', color: '#555', marginBottom: '6px', fontSize: '0.95rem', },
    value: { color: '#333', fontSize: '1rem', lineHeight: '1.5', },
    notSet: { color: '#999', fontStyle: 'italic', },
    input: { width: '100%', padding: '10px 12px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '1rem', boxSizing: 'border-box', },
    buttonGroup: { display: 'flex', justifyContent: 'flex-end', gap: '15px', marginTop: '30px', paddingTop: '20px', borderTop: '1px solid #eee', },
    actionButton: { padding: '10px 20px', borderRadius: '5px', border: 'none', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s ease', display: 'flex', alignItems: 'center', justifyContent: 'center', },
    saveButton: { backgroundColor: '#1a73e8', color: '#fff', },
    cancelButton: { backgroundColor: '#f1f3f4', color: '#5f6368', border: '1px solid #dadce0', },
    secondaryButton: { backgroundColor: 'transparent', color: '#1a73e8', border: '1px solid #1a73e8', ':hover': { backgroundColor: 'rgba(26, 115, 232, 0.05)'} }, // Added basic hover
    errorBox: { color: 'red', backgroundColor: '#ffebee', border: '1px solid red', borderRadius: '4px', padding: '10px', marginTop: '15px', marginBottom: '15px', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '8px', },
    '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } },
     // --- Include Password Modal Styles (copied from ChangePasswordModal component example) ---
     modalOverlayStyle: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1050, },
     passwordModal: {
        content: { backgroundColor: '#fff', padding: '30px 35px', borderRadius: '8px', maxWidth: '500px', width: '90%', position: 'relative', boxShadow: '0 5px 15px rgba(0,0,0,0.2)', },
        closeButton: { position: 'absolute', top: '15px', right: '15px', background: 'none', border: 'none', fontSize: '1.3rem', cursor: 'pointer', color: '#888', },
        title: { marginTop: 0, marginBottom: '25px', color: '#1a237e', borderBottom: '1px solid #eee', paddingBottom: '15px', fontSize: '1.5rem', display: 'flex', alignItems: 'center' },
        fieldGroup: { marginBottom: '20px', },
        label: { fontWeight: '600', color: '#555', display: 'block', marginBottom: '8px', fontSize: '0.95rem', },
        buttonGroup: { display: 'flex', justifyContent: 'flex-end', gap: '15px', marginTop: '25px', },
    },
};

export default Profile;